import React from "react";
import { PropTypes } from "prop-types";
import playerLoader from "../../utils/playerLoader";
import style from "./PlayerShadowDom.module.scss";

/**
 * Renders the WebGL player inside a shadowDOM.
 *
 * Usage :
 * ```js
 * <PlayerShadowDom
 *   onPlayerCreated={player => this.onPlayerCreated(player)}
 *   size='normal'
 *   title={activity.title}
 *   appBar
 * />
 * ```
 * @arguments {Component<Props, State>}
 *
 */

class PlayerShadowDom extends React.Component {
  player = null;
  playerContainer = null;
  version = process.env.REACT_APP_PLAYER_1_VERSION;
  stage = process.env.REACT_APP_PLAYER_1_ENV;

  async componentDidMount() {
    const shadow = this.buildShadow();

    const entrypoints = [
      `https://dev.vivedlearning.com/${this.stage}/${this.version}/main.js`,
      `https://dev.vivedlearning.com/${this.stage}/${this.version}/style.css`,
    ];
    await playerLoader(entrypoints, shadow);

    this.startPlayer(shadow);
  }

  buildShadow() {
    let shadowParent = document.getElementById("player-shadow");
    let shadow = shadowParent.attachShadow({ mode: "open" });

    shadow.innerHTML = `
    <link type="text/css" href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
    <style> .player-container{ display: flex; position: relative; height: 100%; width: 100%; } </style>
    <div id="player-container-authoring" class="player-container" />
    `;

    this.playerContainer = shadow.getElementById("player-container-authoring");

    Object.defineProperty(this.playerContainer, "ownerDocument", {
      value: shadow,
    });
    shadow.createElement = (...args) => document.createElement(...args);
    shadow.createElementNS = (...args) => document.createElementNS(...args);
    shadow.createTextNode = (...args) => document.createTextNode(...args);

    return shadow;
  }

  startPlayer(shadow) {
    let options = {
      ...this.props.playerOptions,
      playerID: "1acc340f-cf40-44eb-9e1d-5607c1ae4f07",
      capabilities: {
        enabled: true,
        name: "Ouzel Player",
        features: {
          theaterDisplaySupported: false,
          developSupported: false,
          presentationModeIsSupported: false,
          screenshotsSupported: true,
          customCacheSupported: false,
          hideHelpDocument: false,
          hideFeedbackWidget: false,
          narration: false,
          allowHMD: false,
          allowZSpace: true,
        },
      },
    };

    this.player = window.VIVEDPlayer.mount(
      this.playerContainer,
      options,
      shadow
    );

    this.props.onPlayerCreated(this.player);
  }

  componentWillUnmount() {
    if (this.player) {
      this.player.dispose();
    }

    if (this.playerContainer) {
      window.VIVEDPlayer.unmount(this.playerContainer);
    }
  }

  render() {
    const classes = [
      style.container,
      this.props.size === "normal" ? style.containerNormal : null,
      this.props.size === "freeplay" ? style.containerFreeplayFullsize : null,
      this.props.size === "authoring" ? style.containerAuthoring : null,
      this.props.size === "embed" ? style.containerEmbed : null,
      this.props.size === "fill" ? style.containerFill : null,
    ].join(" ");
    return <div id="player-shadow" className={classes} />;
  }
}

PlayerShadowDom.defaultProps = {
  appBar: false,
};

PlayerShadowDom.propTypes = {
  appBar: PropTypes.bool,
  onPlayerCreated: PropTypes.func.isRequired,
  playerOptions: PropTypes.any,
  size: PropTypes.oneOf(["normal", "freeplay", "authoring", "embed", "fill"])
    .isRequired,
  title: PropTypes.string,
};

export default PlayerShadowDom;
